import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AnnoSelector from "@/components/AnnoSelector";
import AlunniSendBar from "@/components/AlunniSendBar";
import useAlunniSelection from "@/lib/useAlunniSelection";

export default function PrenotazioniLibriPage() {
  const { annoCorrente } = useAuth();
  const [data, setData] = useState({ anno: null, items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSet, setFilterSet] = useState("");
  const [filterCla, setFilterCla] = useState("");
  const [filterCiclo, setFilterCiclo] = useState("");
  const [filterDal, setFilterDal] = useState("");
  const [filterAl, setFilterAl] = useState("");
  const [filterIncassate, setFilterIncassate] = useState(""); // "", "true", "false"
  // Ordinamento lista: "cognome" (default) | "ric_n" | "ric_data_n"
  const [sortBy, setSortBy] = useState("cognome");
  const [msg, setMsg] = useState(null);
  const sel = useAlunniSelection();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (annoCorrente) params.anno = annoCorrente;
      if (filterDal) params.dal = filterDal;
      if (filterAl) params.al = filterAl;
      // Solo i valori "inc_true"/"inc_false" arrivano al backend come param `incassate`
      if (filterIncassate === "inc_true") params.incassate = "true";
      else if (filterIncassate === "inc_false") params.incassate = "false";
      const r = await api.get("/prenotazioni-libri/lista", { params });
      setData(r.data);
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore caricamento" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [annoCorrente, filterDal, filterAl, filterIncassate]);

  const setoriList = useMemo(() => {
    const s = new Set(data.items.map(it => it.SetNom).filter(Boolean));
    return Array.from(s).sort();
  }, [data.items]);
  const classiList = useMemo(() => {
    const s = new Set(data.items.filter(it => !filterSet || it.SetNom === filterSet).map(it => it.ClaNom).filter(Boolean));
    return Array.from(s).sort();
  }, [data.items, filterSet]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // 🆕 Feb 2026: due opzioni client-side per filtrare per stato saldo libreria
    const soloSaldati = filterIncassate === "sal_true";
    const soloDaSaldare = filterIncassate === "sal_false";
    return data.items.filter(it => {
      if (filterSet && it.SetNom !== filterSet) return false;
      if (filterCla && it.ClaNom !== filterCla) return false;
      if (filterCiclo) {
        if (filterCiclo === "_NONE_") {
          if (it.ciclo) return false;
        } else if (it.ciclo !== filterCiclo) return false;
      }
      if (soloSaldati && !it.saldato_libreria_at) return false;
      // "Solo DA saldare": ricevuta emessa MA campo saldato libreria vuoto
      if (soloDaSaldare && (!it.ricevuta_n || it.saldato_libreria_at)) return false;
      if (q && !(`${it.Cog} ${it.Nom}`.toLowerCase().includes(q) || String(it.Cod).includes(q))) return false;
      return true;
    });
  }, [data.items, search, filterSet, filterCla, filterCiclo, filterIncassate]);

  // Ordinamento applicato sopra al filtraggio: tre modalità (UTENTE feb-2026)
  // - cognome: Cog → Nom (default, classico)
  // - ric_n: numero ricevuta ascendente (le righe senza ricevuta in fondo)
  // - ric_data_n: data ricevuta ascendente, poi numero ricevuta (no ricevuta in fondo)
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), "it", { sensitivity: "base" });
    if (sortBy === "ric_n") {
      arr.sort((a, b) => {
        const na = Number(a.ricevuta_n) || Infinity;
        const nb = Number(b.ricevuta_n) || Infinity;
        if (na !== nb) return na - nb;
        return cmpStr(a.Cog, b.Cog) || cmpStr(a.Nom, b.Nom);
      });
    } else if (sortBy === "ric_data_n") {
      arr.sort((a, b) => {
        const da = a.ricevuta_emessa_at || "";
        const db = b.ricevuta_emessa_at || "";
        // Le ricevute non emesse vanno in fondo
        if (!da && db) return 1;
        if (da && !db) return -1;
        if (da !== db) return da < db ? -1 : 1;
        const na = Number(a.ricevuta_n) || Infinity;
        const nb = Number(b.ricevuta_n) || Infinity;
        if (na !== nb) return na - nb;
        return cmpStr(a.Cog, b.Cog) || cmpStr(a.Nom, b.Nom);
      });
    } else {
      // default: cognome → nome
      arr.sort((a, b) => cmpStr(a.Cog, b.Cog) || cmpStr(a.Nom, b.Nom));
    }
    return arr;
  }, [filtered, sortBy]);

  // Conteggi per ciascuna tab ciclo (sui dati grezzi, non sui filtrati)
  const cicliCounts = useMemo(() => {
    const c = {};
    for (const it of data.items) {
      const k = it.ciclo || "_NONE_";
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [data.items]);

  const totals = useMemo(() => {
    let nCop = 0, nAcc = 0, totAcc = 0, nRic = 0, nSaldati = 0;
    for (const it of filtered) {
      if (it.copertine) nCop++;
      if (it.acconto_libri > 0) { nAcc++; totAcc += it.acconto_libri; }
      if (it.ricevuta_n) nRic++;
      if (it.saldato_libreria_at) nSaldati++;
    }
    return { nCop, nAcc, totAcc, nRic, nSaldati };
  }, [filtered]);

  const updateField = async (cod, field, value) => {
    try {
      const params = annoCorrente ? { anno: annoCorrente } : {};
      await api.put(`/prenotazioni-libri/${cod}`, { [field]: value }, { params });
      setData(d => ({ ...d, items: d.items.map(it => it.Cod === cod ? { ...it, [field]: value } : it) }));
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    }
  };

  const stampaRicevuta = (cod) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const qs = annoCorrente ? `?anno=${annoCorrente}` : "";
    window.open(`${base}/api/prenotazioni-libri/${cod}/ricevuta-pdf${qs}`, "_blank", "noopener,noreferrer");
    // Ricarica la lista dopo un istante per mostrare numero ricevuta + data
    setTimeout(() => { load(); }, 1500);
  };

  const stampaRegistro = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams();
    if (annoCorrente) params.set("anno", String(annoCorrente));
    if (filterDal) params.set("dal", filterDal);
    if (filterAl) params.set("al", filterAl);
    if (filterIncassate === "inc_true") params.set("incassate", "true");
    else if (filterIncassate === "inc_false") params.set("incassate", "false");
    if (filterCiclo) params.set("ciclo", filterCiclo);
    if (filterSet) params.set("settore", filterSet);
    if (filterCla) params.set("classe", filterCla);
    window.open(`${base}/api/prenotazioni-libri/registro-pdf?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  const annullaRicevuta = async (cod, ricN) => {
    if (!window.confirm(`Annullare la ricevuta ${ricN}?\n\nIl numero NON sarà riutilizzato.\nLa prossima emissione userà il numero successivo.`)) return;
    try {
      const params = annoCorrente ? { anno: annoCorrente } : {};
      await api.delete(`/prenotazioni-libri/${cod}/ricevuta`, { params });
      setMsg({ ok: true, text: `Ricevuta ${ricN} annullata` });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore annullamento ricevuta" });
    }
  };

  // 🆕 Feb 2026: Bulk "Saldato Libreria" — segna N righe selezionate con la data scelta
  const [saldoBulkDate, setSaldoBulkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const bulkSaldoLibreria = async (action) => {
    const cods = Array.from(sel.selected || []);
    if (cods.length === 0) { setMsg({ ok: false, text: "Nessuna prenotazione selezionata" }); return; }
    if (action === "set" && !saldoBulkDate) { setMsg({ ok: false, text: "Scegli una data" }); return; }
    const label = action === "set"
      ? `Confermi di segnare ${cods.length} prenotazioni come saldate alla libreria in data ${fmtDate(saldoBulkDate)}?`
      : `Confermi di rimuovere la marcatura 'saldato libreria' da ${cods.length} prenotazioni?`;
    if (!window.confirm(label)) return;
    try {
      const params = annoCorrente ? { anno: annoCorrente } : {};
      const r = await api.post("/prenotazioni-libri/saldo-libreria/bulk",
        { alunni_cod: cods, data: saldoBulkDate, action },
        { params });
      setMsg({ ok: true, text: action === "set"
        ? `${r.data.modified || cods.length} prenotazioni marcate saldate in data ${fmtDate(saldoBulkDate)}`
        : `Marcatura rimossa da ${r.data.modified || cods.length} prenotazioni` });
      sel.clear();
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore aggiornamento saldo libreria" });
    }
  };

  // Format ISO date string → DD/MM/YYYY
  const fmtDate = (iso) => {
    if (!iso) return "";
    const s = String(iso).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  return (
    <div data-testid="prenotazioni-libri-page">
      <div className="sw-page-title">
        <h1>📚 Prenotazioni Libri di Testo</h1>
        <div className="sw-breadcrumb">Home &gt; Prenotazioni Libri</div>
      </div>

      <div className="sw-card">
        <div className="sw-card-body">
          {/* Tab Ciclo curriculum (richiesta utente 2026-02: dividere lista per I/P1-5/M1-3/U) */}
          <CicloTabs filterCiclo={filterCiclo} setFilterCiclo={setFilterCiclo} cicliCounts={cicliCounts} totale={data.items.length} />
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8 }}>
            <AnnoSelector testId="prenot-anno-attivo" />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Cerca:</span>
            <input data-testid="prenot-search" type="text" placeholder="cognome, nome, cod..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="sw-filter-input" style={{ width: 200 }} />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Settore:</span>
            <select className="sw-filter-select" value={filterSet} onChange={e => { setFilterSet(e.target.value); setFilterCla(""); }} style={{ minWidth: 100 }}>
              <option value="">Tutti</option>
              {setoriList.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Classe:</span>
            <select className="sw-filter-select" value={filterCla} onChange={e => setFilterCla(e.target.value)} style={{ minWidth: 100 }}>
              <option value="">Tutte</option>
              {classiList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {(search || filterSet || filterCla || filterCiclo || filterDal || filterAl || filterIncassate) && <button className="sw-filter-btn" onClick={() => { setSearch(""); setFilterSet(""); setFilterCla(""); setFilterCiclo(""); setFilterDal(""); setFilterAl(""); setFilterIncassate(""); }}>Pulisci</button>}
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#444" }}>
              <b>{filtered.length}</b> alunni · <b>{totals.nAcc}</b> con acconto (tot. € {totals.totAcc.toLocaleString("it-IT", { minimumFractionDigits: 2 })}) · <b>{totals.nCop}</b> con copertine · <b>{totals.nRic}</b> ricevute · <b style={{ color: "#6a1b9a" }}>{totals.nSaldati}</b> saldate lib.
            </span>
          </div>
          {/* Riga filtri ricevute */}
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8, marginTop: 6 }}>
            <span className="sw-filter-label" style={{ color: "#1565c0", fontWeight: 700 }}>🖨 Ricevute:</span>
            <span className="sw-filter-label">Data dal:</span>
            <input data-testid="prenot-ric-dal" type="date" value={filterDal}
              onChange={e => setFilterDal(e.target.value)}
              className="sw-filter-input" style={{ width: 140 }} />
            <span className="sw-filter-label">al:</span>
            <input data-testid="prenot-ric-al" type="date" value={filterAl}
              onChange={e => setFilterAl(e.target.value)}
              className="sw-filter-input" style={{ width: 140 }} />
            <span data-testid="prenot-ric-stato-label" className="sw-filter-label" style={{ marginLeft: 8 }}>Stato:</span>
            <select data-testid="prenot-ric-stato" className="sw-filter-select"
              value={filterIncassate} onChange={e => setFilterIncassate(e.target.value)}
              style={{ minWidth: 190 }}>
              <option value="">Tutte</option>
              <optgroup label="Ricevuta">
                <option value="inc_true">Solo incassate</option>
                <option value="inc_false">Solo non incassate</option>
              </optgroup>
              <optgroup label="Libreria">
                <option value="sal_true">Solo saldate libreria</option>
                <option value="sal_false">Solo DA saldare libreria (ric. emessa)</option>
              </optgroup>
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Ordina per:</span>
            <select data-testid="prenot-sortby" className="sw-filter-select"
              value={sortBy} onChange={e => setSortBy(e.target.value)}
              title="Modalità di ordinamento della lista"
              style={{ minWidth: 200 }}>
              <option value="cognome">Cognome → Nome</option>
              <option value="ric_n">N° Ricevuta</option>
              <option value="ric_data_n">Data Ricevuta + N° Ricevuta</option>
            </select>
            <button data-testid="prenot-stampa-registro" onClick={stampaRegistro}
              title="Stampa il registro PDF delle ricevute filtrate (con totale acconti)"
              style={{
                marginLeft: "auto",
                padding: "5px 12px",
                background: "#1b5e20",
                color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700,
                cursor: "pointer",
              }}>
              📄 Stampa Registro PDF
            </button>
          </div>
          {msg && <div style={{ marginTop: 8, padding: 8, background: msg.ok ? "#e8f5e9" : "#ffebee", border: `1px solid ${msg.ok ? "#a5d6a7" : "#ef9a9a"}`, fontSize: 12, color: msg.ok ? "#1b5e20" : "#b71c1c" }}>{msg.text}</div>}
        </div>
      </div>

      <AlunniSendBar selected={sel.selected} onClear={sel.clear}
        label={`Prenotazione Libri ${data.anno ? `${data.anno}/${String((data.anno||0)+1).slice(-2)}` : ""}${filterCiclo ? ` · ${filterCiclo}` : ""}`}
        anno={data.anno} />

      {/* 🆕 Feb 2026: Barra bulk "Saldato Libreria" — visibile solo se ci sono righe selezionate */}
      {sel.size > 0 && (
        <div data-testid="prenot-bulk-saldo-bar"
          style={{
            marginTop: 8, padding: "10px 14px",
            background: "linear-gradient(90deg, #f3e5f5, #e1bee7)",
            border: "1.5px solid #8e24aa", borderRadius: 4,
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            boxShadow: "0 2px 6px rgba(142,36,170,0.15)",
          }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#4a148c" }}>
            💰 {sel.size} prenotaz{sel.size === 1 ? "ione" : "ioni"} selezionat{sel.size === 1 ? "a" : "e"} —
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#6a1b9a" }}>Data consegna soldi:</span>
          <input type="date" data-testid="prenot-bulk-saldo-date"
            value={saldoBulkDate} onChange={e => setSaldoBulkDate(e.target.value)}
            style={{ padding: "4px 8px", border: "1.5px solid #8e24aa", borderRadius: 3, fontSize: 12, fontWeight: 600, background: "white" }} />
          <button data-testid="prenot-bulk-saldo-set" onClick={() => bulkSaldoLibreria("set")}
            style={{ padding: "6px 14px", background: "#6a1b9a", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
            💰 Segna saldato
          </button>
          <button data-testid="prenot-bulk-saldo-clear" onClick={() => bulkSaldoLibreria("clear")}
            title="Rimuove la marcatura 'saldato libreria' dalle prenotazioni selezionate"
            style={{ padding: "6px 12px", background: "white", color: "#6a1b9a", border: "1.5px solid #6a1b9a", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            ✕ Rimuovi saldato
          </button>
          <button onClick={() => sel.clear()}
            style={{ padding: "5px 10px", background: "transparent", color: "#4a148c", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
            Deseleziona tutti
          </button>
        </div>
      )}

      <div className="sw-card" style={{ marginTop: 8 }}>
        <div style={{ overflow: "auto" }}>
          <table className="sw-table">
            <thead className="thead-blue">
              <tr>
                <th style={{ width: 30, textAlign: "center" }}>
                  <input type="checkbox" data-testid="prenot-selall"
                    title="Seleziona/Deseleziona tutti i visibili"
                    checked={filtered.length > 0 && filtered.every(it => sel.isSelected(it.Cod))}
                    onChange={(e) => e.target.checked ? sel.selectAll(filtered.map(it => it.Cod)) : sel.deselectAll(filtered.map(it => it.Cod))} />
                </th>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 60 }}>Cod</th>
                <th style={{ width: 50, textAlign: "center" }} title="Ciclo curriculum (I/P1-P5/M1-M3/U)">CICLO</th>
                <th>Cognome</th>
                <th>Nome</th>
                <th style={{ width: 150 }}>Comune</th>
                <th style={{ width: 80 }}>Settore</th>
                <th style={{ width: 80 }}>Classe</th>
                <th style={{ width: 130, textAlign: "center" }}>Acconto Libri (€)</th>
                <th style={{ width: 100, textAlign: "center" }}>Copertine</th>
                <th style={{ width: 110, textAlign: "center" }} title="Data emissione ricevuta">Data Ricevuta</th>
                <th style={{ width: 130, textAlign: "center" }} title="Numero ricevuta emessa">N° Ricevuta</th>
                <th style={{ width: 115, textAlign: "center", background: "#f3e5f5", color: "#4a148c" }}
                    title="Data in cui i soldi sono stati consegnati alla Libreria Ferrata (aggiornabile in blocco selezionando più righe)">💰 Saldato Lib.</th>
                <th style={{ width: 130, textAlign: "center" }}>Stampa</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={15} className="sw-loading">Caricamento…</td></tr>
                : filtered.length === 0 ? <tr><td colSpan={15} className="sw-empty"><p>Nessun alunno trovato</p></td></tr>
                  : sorted.map((it, i) => (
                    <tr key={it.Cod} data-testid={`prenot-row-${it.Cod}`}
                        style={{ background: sel.isSelected(it.Cod) ? "#f3e5f5" : undefined }}>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" data-testid={`prenot-sel-${it.Cod}`}
                          checked={sel.isSelected(it.Cod)} onChange={() => sel.toggle(it.Cod)} />
                      </td>
                      <td style={{ color: "#888", textAlign: "center" }}>{i + 1}</td>
                      <td style={{ textAlign: "center", fontFamily: "Consolas" }}>{it.Cod}</td>
                      <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 800, color: "#4a148c" }}
                          data-testid={`prenot-ciclo-${it.Cod}`}>
                        {it.ciclo || <span style={{ color: "#ccc" }}>—</span>}
                      </td>
                      <td style={{ fontWeight: 600 }}>{it.Cog}</td>
                      <td>{it.Nom}</td>
                      <td style={{ color: "#555" }} data-testid={`prenot-comune-${it.Cod}`}>{it.ResLuo ? `${it.ResLuo}${it.ResPro ? ` (${it.ResPro})` : ""}` : "-"}</td>
                      <td style={{ textAlign: "center" }}>{it.SetNom || "-"}</td>
                      <td style={{ textAlign: "center", fontWeight: 600 }}>{it.ClaNom || "-"}</td>
                      <td style={{ textAlign: "center" }}>
                        <AccontoInput cod={it.Cod} value={it.acconto_libri} onSave={(v) => updateField(it.Cod, "acconto_libri", v)} />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" data-testid={`prenot-cop-${it.Cod}`}
                          checked={!!it.copertine}
                          onChange={e => updateField(it.Cod, "copertine", e.target.checked)}
                          style={{ width: 18, height: 18, cursor: "pointer" }} />
                      </td>
                      <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 11, color: it.ricevuta_emessa_at ? "#1b5e20" : "#bbb" }}
                          data-testid={`prenot-ric-data-${it.Cod}`}>
                        {fmtDate(it.ricevuta_emessa_at) || "—"}
                      </td>
                      <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 11, fontWeight: 700, color: it.ricevuta_n ? "#1565c0" : "#bbb" }}
                          data-testid={`prenot-ric-n-${it.Cod}`}>
                        {it.ricevuta_n ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span>{it.ricevuta_n}</span>
                            <button
                              data-testid={`prenot-ric-annulla-${it.Cod}`}
                              onClick={() => annullaRicevuta(it.Cod, it.ricevuta_n)}
                              title={`Annulla ricevuta ${it.ricevuta_n}`}
                              style={{
                                width: 18, height: 18, border: "none", borderRadius: 2,
                                background: "#c62828", color: "white", cursor: "pointer",
                                fontSize: 11, fontWeight: 800, lineHeight: "16px",
                                padding: 0,
                              }}>
                              ✕
                            </button>
                          </span>
                        ) : "—"}
                      </td>
                      <td style={{ textAlign: "center", background: it.saldato_libreria_at ? "#f3e5f5" : undefined, fontSize: 11, fontFamily: "Consolas, monospace", color: it.saldato_libreria_at ? "#4a148c" : "#bbb", fontWeight: it.saldato_libreria_at ? 700 : 400 }}
                          data-testid={`prenot-saldo-lib-${it.Cod}`}
                          title={it.saldato_libreria_at ? `Saldato alla libreria in data ${fmtDate(it.saldato_libreria_at)}${it.saldato_libreria_by ? ` da ${it.saldato_libreria_by}` : ""}` : "Non ancora saldato alla libreria"}>
                        {it.saldato_libreria_at ? fmtDate(it.saldato_libreria_at) : "—"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button onClick={() => stampaRicevuta(it.Cod)} data-testid={`prenot-ricevuta-${it.Cod}`}
                          disabled={!(it.acconto_libri > 0 || it.copertine)}
                          title={it.ricevuta_n ? `Ristampa ricevuta ${it.ricevuta_n}` : "Stampa ricevuta PDF"}
                          style={{
                            padding: "4px 10px",
                            background: (it.acconto_libri > 0 || it.copertine) ? (it.ricevuta_n ? "#37474f" : "#1565c0") : "#bdbdbd",
                            color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700,
                            cursor: (it.acconto_libri > 0 || it.copertine) ? "pointer" : "not-allowed",
                          }}>
                          {it.ricevuta_n ? "🖨 Ristampa" : "🖨 Ricevuta"}
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AccontoInput({ cod, value, onSave }) {
  const [v, setV] = useState(value || 0);
  useEffect(() => { setV(value || 0); }, [value]);
  return (
    <input
      type="number"
      min={0}
      step={0.5}
      value={v}
      data-testid={`prenot-acconto-${cod}`}
      onChange={e => setV(Number(e.target.value))}
      onBlur={() => { if (Number(v) !== Number(value)) onSave(Number(v)); }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      style={{ width: 90, padding: "3px 6px", textAlign: "right", border: "1px solid #cfd8dc", borderRadius: 2, fontSize: 12, fontWeight: 600 }}
    />
  );
}


// Tab di filtro per Ciclo curriculum (I / P1-P5 / M1-M3 / U / ?).
// Coerente con il pattern usato in Comunicazioni e Curriculum.
function CicloTabs({ filterCiclo, setFilterCiclo, cicliCounts, totale }) {
  const COLORS = {
    "":          { c: "#37474f", bg: "#eceff1" },
    "_NONE_":    { c: "#37474f", bg: "#eceff1" },
    "I":         { c: "#7b1fa2", bg: "#f3e5f5" },
    "P1":        { c: "#2e7d32", bg: "#e8f5e9" },
    "P2":        { c: "#2e7d32", bg: "#e8f5e9" },
    "P3":        { c: "#2e7d32", bg: "#e8f5e9" },
    "P4":        { c: "#2e7d32", bg: "#e8f5e9" },
    "P5":        { c: "#2e7d32", bg: "#e8f5e9" },
    "M1":        { c: "#1565c0", bg: "#e3f2fd" },
    "M2":        { c: "#1565c0", bg: "#e3f2fd" },
    "M3":        { c: "#1565c0", bg: "#e3f2fd" },
    "U":         { c: "#b71c1c", bg: "#ffebee" },
  };
  const tabs = [
    { k: "",     l: "Tutti", count: totale },
    { k: "_NONE_", l: "?",   count: cicliCounts["_NONE_"] || 0 },
    { k: "I",    l: "I",     count: cicliCounts["I"]    || 0 },
    { k: "P1",   l: "P1",    count: cicliCounts["P1"]   || 0 },
    { k: "P2",   l: "P2",    count: cicliCounts["P2"]   || 0 },
    { k: "P3",   l: "P3",    count: cicliCounts["P3"]   || 0 },
    { k: "P4",   l: "P4",    count: cicliCounts["P4"]   || 0 },
    { k: "P5",   l: "P5",    count: cicliCounts["P5"]   || 0 },
    { k: "M1",   l: "M1",    count: cicliCounts["M1"]   || 0 },
    { k: "M2",   l: "M2",    count: cicliCounts["M2"]   || 0 },
    { k: "M3",   l: "M3",    count: cicliCounts["M3"]   || 0 },
    { k: "U",    l: "U",     count: cicliCounts["U"]    || 0 },
  ];
  return (
    <div data-testid="prenot-ciclo-tabs"
         style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8, paddingBottom: 6, borderBottom: "2px solid #1565c0" }}>
      {tabs.map(t => {
        const active = filterCiclo === t.k;
        const col = COLORS[t.k] || COLORS[""];
        const dim = !active && t.count === 0;
        return (
          <button key={t.k || "all"} onClick={() => setFilterCiclo(t.k)}
            data-testid={`prenot-tab-ciclo-${t.k || "all"}`}
            disabled={dim}
            style={{
              padding: "6px 12px",
              background: active ? col.c : col.bg,
              color: active ? "white" : col.c,
              border: `1px solid ${col.c}`,
              borderRadius: 3,
              fontWeight: 800,
              fontSize: 12,
              cursor: dim ? "default" : "pointer",
              opacity: dim ? 0.35 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}>
            <span>{t.l}</span>
            <span style={{
              background: active ? "rgba(255,255,255,0.25)" : "white",
              color: active ? "white" : col.c,
              padding: "0 6px", borderRadius: 8, fontSize: 10, fontWeight: 800,
            }}>{t.count}</span>
          </button>
        );
      })}
    </div>
  );
}
